import { select } from 'd3-selection';
import React, { useEffect, useRef, Dispatch, SetStateAction, useContext, useCallback } from 'react';

import { fontStyles } from '@/styles/typography';

import { Route } from '@/utils/0x.types';

import { classNames } from '../../utils/classnames';
import { createDisplaySankeyDiagramSvg, Margin } from './create';
import { SankeyContext } from './SankeyProvider';
import { SubRoute } from './types';

interface SankeyChartProps {
  route: Route;
  chainId: number;
  width: number;
  height: number;
  isMobile: boolean;
  onSubRouteTouch: Dispatch<SetStateAction<SubRoute | undefined>>;
  isPreview?: boolean;
  className?: string;
  margin?: Margin;
}

export const SankeyDiagram: React.FC<SankeyChartProps> = ({
  route,
  chainId,
  width,
  height,
  isMobile,
  onSubRouteTouch,
  isPreview = false,
  className,
  margin,
}) => {
  const sankeyRef = useRef<HTMLDivElement | null>(null);
  const { liquiditySourceOnHoverInLegend } = useContext(SankeyContext);

  const updateSankeyDiagram = useCallback(async () => {
    if (!width || !height) return;

    const svg = await createDisplaySankeyDiagramSvg(
      route,
      chainId,
      width,
      height,
      isMobile,
      isPreview,
      onSubRouteTouch,
      sankeyRef,
      liquiditySourceOnHoverInLegend,
      margin,
    );
    const svgNode = svg?.node();
    if (!svgNode || !(svgNode instanceof Node)) return;

    const svgContainer = select(sankeyRef.current);
    svgContainer.selectAll('svg').remove();
    svgContainer.node()?.appendChild(svgNode);
  }, [
    route,
    chainId,
    width,
    height,
    isMobile,
    isPreview,
    onSubRouteTouch,
    liquiditySourceOnHoverInLegend,
    margin,
  ]);

  useEffect(() => {
    updateSankeyDiagram();
  }, [updateSankeyDiagram]);

  return (
    <div
      ref={sankeyRef}
      className={classNames(
        'bg-token-gray-200 rounded-[1rem] border-solid relative w-full h-full after:content-[""] after:absolute after:h-full after:w-full after:top-0 after:pointer-events-none after:left-0 after:shadow-inner-05 after:rounded-[1rem]',
        className,
      )}
    >
      <div id="tooltipContainer" className="absolute pointer-events-none hidden">
        <div
          id="tooltipContent"
          className={fontStyles({
            variant: 'LABEL_SM',
            class: 'bg-special-background-tooltip rounded-md shadow-25',
          })}
        ></div>
      </div>
    </div>
  );
};
